import VueRouter from "vue-router";
import { getCurrentHub } from "@jupiter/hub";
import { Integration } from "@jupiter/types";
import { basename, Logger } from "@jupiter/utils";
import { BrowserOptions } from "../types/options";
import { EventBuilder } from "@jupiter/event";

export interface VueOptions extends BrowserOptions {
  Vue?: VueInstance;
}

export interface VueRouterOptions extends BrowserOptions {
  VueRouter?: VueRouter;
}

/** Global Vue object limited to the methods/attributes we require */
interface VueInstance {
  config: {
    errorHandler?(error: Error, vm?: ViewModel, info?: string): void;
  };
  util?: {
    warn(...input: any): void;
  };
  mixin(hooks: { [key: string]: () => void }): void;
}

/** Representation of Vue component internals */
interface ViewModel {
  [key: string]: any;
  // eslint-disable-next-line @typescript-eslint/ban-types
  $root: object;
  $options: {
    [key: string]: any;
    name?: string;
    propsData?: { [key: string]: any };
    _componentTag?: string;
    __file?: string;
  };
  $once(hook: string, cb: () => void): void;
}

interface Metadata {
  [key: string]: any;
  componentName?: string;
  propsData?: { [key: string]: any };
  lifecycleHook?: string;
}

const COMPONENT_NAME_REGEXP = /(?:^|[-_/])(\w)/g;
const ROOT_COMPONENT_NAME = "root";
const ANONYMOUS_COMPONENT_NAME = "anonymous component";

export class VueHandler implements Integration {
  /**
   * @inheritDoc
   */
  public static id: string = "VueHandler";

  /**
   * @inheritDoc
   */
  public name: string = VueHandler.id;

  /**
   * Cache holding already processed component names
   */
  private readonly _componentsCache: { [key: string]: string } = {};
  private _options: VueOptions;

  /**
   * @inheritDoc
   */
  public constructor(options: VueOptions) {
    this._options = options;
  }

  /**
   * Attaches the error handler and starts tracing
   */
  public setupOnce(): void {
    if (this._options.Vue === undefined) {
      Logger.warn(
        "No Vue instance was provided. Skip VueHandler initialization"
      );
    } else {
      this._attachErrorHandler();
    }
  }

  /**
   * Extract component name from the ViewModel
   */
  private _getComponentName(vm: ViewModel): string {
    // Such level of granularity is most likely not necessary, but better safe than sorry. — Kamil
    if (!vm) {
      return ANONYMOUS_COMPONENT_NAME;
    }

    if (vm.$root === vm) {
      return ROOT_COMPONENT_NAME;
    }

    if (!vm.$options) {
      return ANONYMOUS_COMPONENT_NAME;
    }

    if (vm.$options.name) {
      return vm.$options.name;
    }

    if (vm.$options._componentTag) {
      return vm.$options._componentTag;
    }

    // injected by vue-loader
    if (vm.$options.__file) {
      const unifiedFile = vm.$options.__file
        .replace(/^[a-zA-Z]:/, "")
        .replace(/\\/g, "/");
      const filename = basename(unifiedFile, ".vue");
      return (
        this._componentsCache[filename] ||
        (this._componentsCache[
          filename
        ] = filename.replace(COMPONENT_NAME_REGEXP, (_, c: string) =>
          c ? c.toUpperCase() : ""
        ))
      );
    }

    return ANONYMOUS_COMPONENT_NAME;
  }

  private _attachErrorHandler(): void {
    const currentErrorHandler = this._options.Vue!.config.errorHandler;
    const vue = this._options.Vue!;

    vue.config.errorHandler = (
      error: Error,
      vm?: ViewModel,
      info?: string
    ): void => {
      const metadata: Metadata = {};

      if (vm) {
        try {
          metadata.componentName = this._getComponentName(vm);
          metadata.propsData = vm.$options.propsData;
        } catch (_oO) {
          Logger.warn("Unable to extract metadata from Vue component.");
        }
      }

      if (info) {
        metadata.lifecycleHook = info;
      }

      // Capture exception in the next event loop, to make sure that all breadcrumbs are recorded in time.
      setTimeout(() => {
        const { stack, message } = error;
        const event = EventBuilder.createExceptionEvent(stack, {
          name: "vue error",
          message,
          ...metadata,
        });
        getCurrentHub().track(event);
      });

      if (typeof currentErrorHandler === "function") {
        currentErrorHandler.call(vue, error, vm, info);
      }

      vue.util &&
        vue.util.warn(`Error in ${info}: "${error && error.toString()}"`, vm);
      console.error(error);
    };
  }
}

export class VueRouterHandler implements Integration {
  /**
   * @inheritDoc
   */
  public static id: string = "VueRouterHandler";

  /**
   * @inheritDoc
   */
  public name: string = VueRouterHandler.id;

  private _options: VueRouterOptions;

  /**
   * @inheritDoc
   */
  public constructor(options: VueRouterOptions) {
    this._options = options;
  }

  public getVueRouter() {
    return this._options.VueRouter;
  }

  /**
   * Attaches the error handler and starts tracing
   */
  public setupOnce(): void {
    if (this._options.VueRouter === undefined) {
      Logger.warn(
        "No VueRouter instance was provided. Skip VueRouterHandler initialization"
      );
    } else {
      this._attachErrorHandler();
    }
  }

  private _attachErrorHandler(): void {
    const router = this._options.VueRouter!;
    router.onError((error: any) => {
      const { message, stack } = error;
      const event = EventBuilder.createExceptionEvent(stack, {
        name: "vue-router error",
        message,
      });
      getCurrentHub().track(event);
    });

    router.beforeEach((to, from, next) => {
      const data = {
        params: to.params,
        query: to.query,
      };

      const event = EventBuilder.createPageViewEvent(this._formattingFromUrl(from.path), to.path, data);
      getCurrentHub().track(event);

      next();
    });
  }
  private _formattingFromUrl(url: string) {
    const formattingFromUrl = url.replace(/^\//ig,'')
    return formattingFromUrl;
  }
}
